import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../lib/api/auth';
import type { ApiError } from '../lib/api';
// PWA install prompt removed per Issue #606
import Button from '../components/Button';
import Card from '../components/Card';
import { Check, X } from 'lucide-react';
import { LOADING_MESSAGES, SPINNER } from '../config/loadingMessages';
import { markTermsAccepted } from '../lib/termsAcceptance';
import { handOffPendingNavigation } from '../lib/pendingNavigation';
import { toSafeRedirectPath } from '../lib/safeRedirect';
import { adoptLogin } from '../lib/accountGuard';

// The destination is written here (Tab B) and read by OnboardingModal (also
// Tab B); key and writer live in lib/pendingNavigation (#830).

function postLoginPath(
  user: { onboarding_completed: boolean; is_platform_admin: boolean },
  pendingRedirect: string | null,
): string {
  // #895: Honor redirect_url for invite links - auto-accept happens on that page
  const isInviteRedirect = pendingRedirect?.startsWith('/invite/');

  if (!user.onboarding_completed && !isInviteRedirect) {
    return '/home'; // OnboardingModal shows automatically (#691)
  }
  if (user.is_platform_admin && !pendingRedirect) return '/platform-admin/home';
  // The original destination (invite page, event, etc.), else /home, which
  // becomes /clubs/:primarySlug via HomeRedirect.
  return pendingRedirect ?? '/home';
}

export default function MagicLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string | null>(null);
  const [redirectMessage, setRedirectMessage] = useState<string>('Welcome back');

  // Use ref to track execution state that persists across effect runs
  const hasExecutedRef = useRef(false);
  const isExecutingRef = useRef(false);

  // Timeout cleanup refs (Frank's feedback)
  const installTimeoutRef = useRef<number | null>(null);
  const redirectTimeoutRef = useRef<number | null>(null);

  // Loading message - pick once on mount (#748)
  const [loadingMessage] = useState(
    () => LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]
  );
  const [spin, setSpin] = useState(0);

  // Check for reduced motion preference (#750)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Spinner animation for verifying state (disabled if reduced motion preferred)
  useEffect(() => {
    if (status !== 'verifying' || prefersReducedMotion) return;
    const interval = setInterval(() => {
      setSpin(s => (s + 1) % SPINNER.length);
    }, 300);
    return () => clearInterval(interval);
  }, [status, prefersReducedMotion]);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('No token provided');
      return;
    }
    
    // Early return if already executed
    if (hasExecutedRef.current) return;

    const consumeToken = async () => {
      // Prevent concurrent or duplicate executions using refs
      if (isExecutingRef.current) {
        return;
      }
      
      isExecutingRef.current = true;

      try {
        const response = await authApi.consumeMagicLink(token);

        // #3312: the login COMPLETED, so this device stops being asked. Marked
        // here rather than when the box was ticked in LoginModal: a user who
        // ticks and then never opens the emailed link must be asked again, or
        // they would be suppressed on the client with no acceptance recorded
        // on the server. Unconditional and idempotent — a device that was
        // already suppressed simply re-writes the same marker.
        markTermsAccepted();

        // Mark as successfully executed
        hasExecutedRef.current = true;

        // Check if user needs onboarding (Issue #703 Phase 1 - Login Redirect)
        // Use official onboarding_completed flag from Issue #563/#692
        const { user } = response;
        const needsOnboarding = !user.onboarding_completed;
        // #3544: the echoed redirect_url is untrusted; an unsafe value is
        // dropped, which routes like no redirect at all (/home).
        const safeRedirect = toSafeRedirectPath(response.redirect_url);

        // Hand redirect_url to OnboardingModal via sessionStorage (#830). It
        // survives cross-tab via D1, then same-tab via sessionStorage. Only
        // for a user onboarding will cover; otherwise clear any stale key.
        handOffPendingNavigation(safeRedirect, needsOnboarding);
        const destination = postLoginPath(user, safeRedirect);

        // #3881: a different account than this tab last held loads fresh. Before
        // the cache write, so the seed reads as this tab's own login.
        if (adoptLogin(user.id, destination)) return;

        // #1014: Cancel any pending /auth/me requests BEFORE setting user data
        // This prevents a race condition where:
        // 1. Old /auth/me request returns 401 (from before login)
        // 2. But user is now in cache (from consume response)
        // 3. Error handler sees "user + 401" and triggers logout
        await queryClient.cancelQueries({ queryKey: ['me'] });

        // Set user data directly in cache instead of just invalidating
        // This ensures auth state is immediately available when navigating to protected routes
        queryClient.setQueryData(['me'], response.user);

        // Mark stale but don't refetch yet
        await queryClient.invalidateQueries({ queryKey: ['me'], refetchType: 'none' });

        // Invalidate event queries to clear any cached 401 errors (#833)
        // This ensures authenticated users see the correct UI after login
        await queryClient.invalidateQueries({
          queryKey: ['event'],
          refetchType: 'none' // Don't refetch now, just mark stale
        });

        // Set redirect message for success state
        if (needsOnboarding) {
          setRedirectMessage("Let's set up your profile");
        } else {
          setRedirectMessage('Welcome back');
        }
        
        // Set success status only after we're ready to redirect
        setStatus('success');
        
        // PWA install prompt removed per Issue #606
        // Focus on mobile web experience for v1.0
        
        // Redirect after successful login (Issue #703 - Club-First Navigation)
        // Flow: magic-link → redirect_url OR /home → OnboardingModal (if needed) → /clubs/:slug
        redirectTimeoutRef.current = window.setTimeout(() => {
          navigate(destination);
        }, 1500);
      } catch (err) {
        // Only set error state if we haven't already succeeded
        if (!hasExecutedRef.current) {
          // Log error in development only
          if (process.env.NODE_ENV === 'development') {
            console.error('[MagicLogin] Token consumption failed:', err);
          }
          
          // Safely extract error message (Frank's feedback)
          let message = 'Invalid or expired link';
          if (err && typeof err === 'object' && 'message' in err) {
            const errorMessage = (err as ApiError).message;
            if (errorMessage) {
              message = errorMessage;
            }
          }
          
          setStatus('error');
          setError(message);
        }
      } finally {
        isExecutingRef.current = false;
      }
    };

    // Execute immediately - no delay needed with ref-based guards
    consumeToken();

    // Cleanup function to prevent state updates after unmount
    return () => {
      hasExecutedRef.current = true; // Prevent any further executions
      
      // Clear timeouts to prevent post-unmount actions (Frank's feedback)
      if (installTimeoutRef.current !== null) {
        clearTimeout(installTimeoutRef.current);
      }
      if (redirectTimeoutRef.current !== null) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [searchParams, navigate, queryClient]);

  // #3167 (George F2): email clients strip query strings, so a bare /signin with
  // no token is a dead end that hard-fails with "No token provided". Redirect the
  // tokenless /signin to the login form instead. The with-token path (/signin?token=)
  // still consumes via the effect above; only /magic-login keeps its error state.
  if (!searchParams.get('token') && location.pathname === '/signin') {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="max-w-lg mx-auto" aria-live="polite">
      {status === 'verifying' && (
        <div className="min-h-[60vh] flex items-center justify-center" role="status">
          <p className="text-sm text-text-muted">
            {loadingMessage}
            <span className="ml-2 text-accent/60">
              {prefersReducedMotion ? '…' : SPINNER[spin]}
            </span>
          </p>
        </div>
      )}

      {status === 'success' && (
        <Card className="text-center p-12">
          <div className="text-oro-success mb-4 flex justify-center"><Check className="w-12 h-12" /></div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Signed in
          </h2>
          <p className="text-text-secondary">
            {redirectMessage}
          </p>
        </Card>
      )}

      {status === 'error' && (
        <Card className="text-center p-12">
          <div className="text-oro-error mb-4 flex justify-center"><X className="w-12 h-12" /></div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Sign-in failed
          </h2>
          <p className="text-text-secondary mb-6">
            {error || 'This link is invalid or has expired.'}
          </p>
          <Button
            onClick={() => navigate('/')}
          >
            Back to home
          </Button>
        </Card>
      )}
    </div>
  );
}
